import {
  formatMinor,
  INVOICE_STATUS_LABELS,
  type ChartAccount,
  type CustomerRow,
  type InvoiceDocument,
  type JobRow,
  type SaveInvoiceRequest,
} from '@hvac/contracts';
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Field,
  Input,
  Skeleton,
} from '@hvac/ui';
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { useState } from 'react';
import {
  createCustomer,
  invoiceKeys,
  invoiceQuery,
  invoiceSettingsQuery,
  issueInvoice,
  saveInvoice,
  voidInvoice,
} from '../../shared/api/invoicing-queries.js';
import { chartQuery } from '../../shared/api/ledger-queries.js';
import { customersQuery, jobsQuery, partiesKeys } from '../../shared/api/parties-queries.js';
import { sessionQuery } from '../../shared/api/queries.js';
import { dollarsToMinor, minorToDollars } from '../books/books-format.js';
import { InvoiceDocumentView } from './invoice-document.js';

/**
 * Writing an invoice, and looking at one.
 *
 * A draft is a form: customer, job, date, terms, lines. Totals are shown as
 * they will be computed, at the province's rate, but the database computes
 * them again on save and its figures are the ones that stand. Issuing numbers
 * the invoice and signs its income record in one step; from then on the page
 * shows the document, offers to print it, and lets it be voided with a reason.
 */

interface LineDraft {
  key: number;
  description: string;
  quantity: string;
  unitPrice: string;
  taxable: boolean;
  categoryId: string;
}

const blankLine = (key: number): LineDraft => ({
  key,
  description: '',
  quantity: '1',
  unitPrice: '',
  taxable: true,
  categoryId: '',
});

const today = () => new Date().toISOString().slice(0, 10);

export function InvoiceEditorPage() {
  const params = useParams({ strict: false });
  const invoiceId = params.invoiceId ?? null;
  const { data: session } = useSuspenseQuery(sessionQuery());
  const organizationId = session.activeOrganizationId ?? '';
  const existing = useQuery({
    ...invoiceQuery(organizationId, invoiceId ?? ''),
    enabled: invoiceId !== null,
  });

  if (invoiceId !== null && existing.isPending) return <Skeleton className="h-64 w-full" />;
  if (invoiceId !== null && existing.data === undefined) {
    return <Alert tone="danger">That invoice was not found.</Alert>;
  }
  if (
    invoiceId !== null &&
    existing.data !== undefined &&
    existing.data.invoice.status !== 'draft'
  ) {
    return <IssuedInvoice organizationId={organizationId} document={existing.data} />;
  }
  return (
    <DraftEditor
      key={invoiceId ?? 'new'}
      organizationId={organizationId}
      invoiceId={invoiceId}
      initial={existing.data ?? null}
    />
  );
}

function DraftEditor({
  organizationId,
  invoiceId,
  initial,
}: {
  organizationId: string;
  invoiceId: string | null;
  initial: InvoiceDocument | null;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const settings = useQuery(invoiceSettingsQuery(organizationId));
  const customers = useQuery(customersQuery(organizationId));
  const jobs = useQuery(jobsQuery(organizationId));
  const chart = useQuery(chartQuery(organizationId));

  const [customerId, setCustomerId] = useState(initial?.invoice.customerId ?? '');
  const [jobId, setJobId] = useState(initial?.invoice.jobId ?? '');
  const [issuedOn, setIssuedOn] = useState(initial?.invoice.issuedOn ?? today());
  const [termsDays, setTermsDays] = useState<string>(
    initial?.invoice.termsDays === null || initial?.invoice.termsDays === undefined
      ? ''
      : String(initial.invoice.termsDays),
  );
  const [notes, setNotes] = useState(initial?.invoice.notes ?? '');
  const [lines, setLines] = useState<LineDraft[]>(
    initial === null || initial.lines.length === 0
      ? [blankLine(1)]
      : initial.lines.map((line, index) => ({
          key: index + 1,
          description: line.description,
          quantity: line.quantity.replace(/\.?0+$/, ''),
          unitPrice: minorToDollars(line.unitPriceMinor),
          taxable: line.taxable,
          categoryId: line.categoryId ?? '',
        })),
  );
  const [nextKey, setNextKey] = useState(lines.length + 1);
  const [error, setError] = useState<string | null>(null);
  const [addingCustomer, setAddingCustomer] = useState(false);

  const incomeAccounts: ChartAccount[] = (chart.data ?? []).filter(
    (account) => account.accountType === 'revenue',
  );
  const customer = (customers.data ?? []).find((row) => row.id === customerId);
  const rate =
    customer?.isTaxExempt === true
      ? 0
      : settings.data?.salesTaxRate === null || settings.data?.salesTaxRate === undefined
        ? 0
        : Number(settings.data.salesTaxRate);
  const effectiveTerms =
    termsDays === '' ? (settings.data?.defaultTermsDays ?? 30) : Number(termsDays);

  const parsed = lines.map((line) => {
    const unit = dollarsToMinor(line.unitPrice);
    const qty = Number(line.quantity);
    const ok = line.description.trim() !== '' && unit !== null && !unit.startsWith('-') && qty > 0;
    const net = ok ? BigInt(Math.round(qty * Number(unit))) : 0n;
    const tax = ok && line.taxable ? BigInt(Math.round(Number(net) * rate)) : 0n;
    return { line, ok, net, tax };
  });
  const net = parsed.reduce((sum, item) => sum + item.net, 0n);
  const tax = parsed.reduce((sum, item) => sum + item.tax, 0n);
  const ready =
    customerId !== '' &&
    issuedOn !== '' &&
    parsed.length > 0 &&
    parsed.every((item) => item.ok) &&
    net > 0n;

  const body = (): SaveInvoiceRequest => ({
    customerId,
    jobId: jobId === '' ? null : jobId,
    issuedOn,
    termsDays: effectiveTerms,
    notes: notes.trim() === '' ? null : notes.trim(),
    lines: lines.map((line) => ({
      description: line.description.trim(),
      quantity: Number(line.quantity),
      unitPriceMinor: dollarsToMinor(line.unitPrice) ?? '0',
      taxable: line.taxable,
      categoryId: line.categoryId === '' ? null : line.categoryId,
    })),
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: invoiceKeys.all(organizationId) });
    void queryClient.invalidateQueries({ queryKey: ['reports', organizationId] });
    void queryClient.invalidateQueries({ queryKey: ['bookkeeping', organizationId] });
  };

  const save = useMutation({
    mutationFn: () => saveInvoice(organizationId, invoiceId, body()),
    onSuccess: (result) => {
      setError(null);
      invalidate();
      if (invoiceId === null) {
        void navigate({ to: '/invoices/$invoiceId', params: { invoiceId: result.invoiceId } });
      }
    },
    onError: (caught) =>
      setError(caught instanceof Error ? caught.message : 'The draft was not saved.'),
  });

  const issue = useMutation({
    mutationFn: async () => {
      const saved = await saveInvoice(organizationId, invoiceId, body());
      await issueInvoice(organizationId, saved.invoiceId);
      return saved.invoiceId;
    },
    onSuccess: (id) => {
      setError(null);
      invalidate();
      void navigate({ to: '/invoices/$invoiceId', params: { invoiceId: id } });
    },
    // The database names what is missing ("a job on every line"); show it as written.
    onError: (caught) =>
      setError(caught instanceof Error ? caught.message : 'The invoice was not issued.'),
  });

  const update = (key: number, change: Partial<LineDraft>) =>
    setLines((before) => before.map((line) => (line.key === key ? { ...line, ...change } : line)));

  const customerJobs = (jobs.data ?? []).filter(
    (job) => customerId === '' || job.customerId === customerId,
  );

  return (
    <div className="flex flex-col gap-4">
      <div>
        <Link to="/invoices" className="text-sm text-content-muted hover:text-content">
          ← Invoices
        </Link>
        <h1 className="mt-1 text-xl font-semibold text-content">
          {invoiceId === null ? 'New invoice' : 'Draft invoice'}
        </h1>
        <p className="text-sm text-content-muted">
          {settings.data === undefined
            ? ''
            : `Will be numbered ${settings.data.nextInvoiceNumber} when issued.`}
        </p>
      </div>

      {error !== null ? <Alert tone="danger">{error}</Alert> : null}

      <Card>
        <CardHeader title="Who and when" />
        <CardBody className="grid gap-4 sm:grid-cols-2">
          <Field label="Customer" required>
            {({ inputProps }) => (
              <div className="flex flex-col gap-1">
                <select
                  {...inputProps}
                  value={customerId}
                  onChange={(event) => {
                    setCustomerId(event.target.value);
                    setJobId('');
                  }}
                  className="h-10 rounded-[var(--radius-control)] border border-border-strong bg-surface px-3 text-sm"
                >
                  <option value="">Choose a customer…</option>
                  {(customers.data ?? []).map((row) => (
                    <option key={row.id} value={row.id}>
                      {row.displayName}
                      {row.isTaxExempt ? ' (tax exempt)' : ''}
                    </option>
                  ))}
                </select>
                {addingCustomer ? null : (
                  <button
                    type="button"
                    className="self-start text-xs text-brand-700 hover:underline"
                    onClick={() => setAddingCustomer(true)}
                  >
                    New customer
                  </button>
                )}
              </div>
            )}
          </Field>

          <Field label="Job" hint="Optional. Ties the income to the job's costs.">
            {({ inputProps }) => (
              <select
                {...inputProps}
                value={jobId}
                onChange={(event) => setJobId(event.target.value)}
                className="h-10 rounded-[var(--radius-control)] border border-border-strong bg-surface px-3 text-sm"
              >
                <option value="">No job</option>
                {customerJobs.map((job: JobRow) => (
                  <option key={job.id} value={job.id}>
                    {job.jobNumber === null ? '' : `${job.jobNumber} · `}
                    {job.title}
                  </option>
                ))}
              </select>
            )}
          </Field>

          {addingCustomer ? (
            <div className="sm:col-span-2">
              <NewCustomer
                organizationId={organizationId}
                onCreated={(row) => {
                  void queryClient.invalidateQueries({
                    queryKey: partiesKeys.customers(organizationId),
                  });
                  setCustomerId(row.id);
                  setAddingCustomer(false);
                }}
                onCancel={() => setAddingCustomer(false)}
              />
            </div>
          ) : null}

          <Field label="Dated" required>
            {({ inputProps }) => (
              <Input
                {...inputProps}
                type="date"
                value={issuedOn}
                onChange={(event) => setIssuedOn(event.target.value)}
              />
            )}
          </Field>
          <Field
            label="Terms (days)"
            hint={`Due ${effectiveTerms === 0 ? 'on receipt' : `${String(effectiveTerms)} days after the date`}.`}
          >
            {({ inputProps }) => (
              <Input
                {...inputProps}
                inputMode="numeric"
                value={termsDays}
                placeholder={String(settings.data?.defaultTermsDays ?? 30)}
                onChange={(event) => setTermsDays(event.target.value.replace(/\D/g, ''))}
              />
            )}
          </Field>
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          title="Lines"
          description={
            customer?.isTaxExempt === true
              ? 'This customer is tax exempt: no tax is charged.'
              : rate === 0
                ? 'No sales tax rate is on file for this province; tax is zero.'
                : `Tax at ${String(Math.round(rate * 1000) / 10)}% on taxable lines.`
          }
        />
        <CardBody className="flex flex-col gap-3">
          <table className="w-full text-sm tabular">
            <thead>
              <tr className="text-left text-xs text-content-muted">
                <th className="py-1 font-medium">Description</th>
                <th className="w-20 py-1 text-right font-medium">Qty</th>
                <th className="w-32 py-1 text-right font-medium">Unit price</th>
                <th className="w-14 py-1 text-center font-medium">Tax</th>
                <th className="w-44 py-1 font-medium">Account</th>
                <th className="w-28 py-1 text-right font-medium">Amount</th>
                <th className="w-8" />
              </tr>
            </thead>
            <tbody>
              {parsed.map(({ line, ok, net: lineNet }) => (
                <tr key={line.key} className="align-top">
                  <td className="py-1 pr-2">
                    <Input
                      aria-label="Description"
                      value={line.description}
                      onChange={(event) => update(line.key, { description: event.target.value })}
                      placeholder="Furnace service call, 2 hours"
                    />
                  </td>
                  <td className="py-1 pl-2">
                    <Input
                      aria-label="Quantity"
                      inputMode="decimal"
                      className="text-right"
                      value={line.quantity}
                      hasError={!(Number(line.quantity) > 0)}
                      onChange={(event) => update(line.key, { quantity: event.target.value })}
                    />
                  </td>
                  <td className="py-1 pl-2">
                    <Input
                      aria-label="Unit price"
                      inputMode="decimal"
                      className="text-right"
                      value={line.unitPrice}
                      hasError={dollarsToMinor(line.unitPrice) === null}
                      onChange={(event) => update(line.key, { unitPrice: event.target.value })}
                    />
                  </td>
                  <td className="py-3 text-center">
                    <input
                      type="checkbox"
                      aria-label="Taxable"
                      checked={line.taxable}
                      onChange={(event) => update(line.key, { taxable: event.target.checked })}
                    />
                  </td>
                  <td className="py-1 pl-2">
                    <select
                      aria-label="Income account"
                      value={line.categoryId}
                      onChange={(event) => update(line.key, { categoryId: event.target.value })}
                      className="h-10 w-full rounded-[var(--radius-control)] border border-border-strong bg-surface px-2 text-sm"
                    >
                      <option value="">Service revenue</option>
                      {incomeAccounts.map((account) => (
                        <option key={account.id} value={account.id}>
                          {account.code ?? ''} {account.name}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="py-3 text-right">{ok ? formatMinor(lineNet.toString()) : '—'}</td>
                  <td className="py-3 text-right">
                    <button
                      type="button"
                      aria-label="Remove line"
                      className="text-content-muted hover:text-danger"
                      onClick={() =>
                        setLines((before) =>
                          before.length === 1
                            ? before
                            : before.filter((other) => other.key !== line.key),
                        )
                      }
                    >
                      ×
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="flex items-start justify-between gap-4">
            <button
              type="button"
              className="text-sm text-brand-700 hover:underline"
              onClick={() => {
                setLines((before) => [...before, blankLine(nextKey)]);
                setNextKey(nextKey + 1);
              }}
            >
              Add a line
            </button>
            <dl className="w-56 text-sm tabular">
              <div className="flex justify-between py-0.5">
                <dt className="text-content-muted">Subtotal</dt>
                <dd>{formatMinor(net.toString())}</dd>
              </div>
              <div className="flex justify-between py-0.5">
                <dt className="text-content-muted">Tax</dt>
                <dd>{formatMinor(tax.toString())}</dd>
              </div>
              <div className="flex justify-between border-t border-border py-0.5 font-semibold">
                <dt>Total</dt>
                <dd>{formatMinor((net + tax).toString())}</dd>
              </div>
            </dl>
          </div>
          <Field label="Notes to the customer" hint="Printed on the invoice.">
            {({ inputProps }) => (
              <textarea
                {...inputProps}
                value={notes}
                rows={2}
                onChange={(event) => setNotes(event.target.value)}
                className="w-full rounded-[var(--radius-control)] border border-border-strong bg-surface px-3 py-2 text-sm"
              />
            )}
          </Field>
        </CardBody>
        <CardFooter>
          <div className="flex flex-wrap items-center gap-2">
            <Button onClick={() => issue.mutate()} isLoading={issue.isPending} disabled={!ready}>
              Issue invoice
            </Button>
            <Button
              variant="secondary"
              onClick={() => save.mutate()}
              isLoading={save.isPending}
              disabled={!ready}
            >
              Save draft
            </Button>
            <span className="text-xs text-content-muted">
              Issuing numbers it and signs its income record. A draft can be changed; an issued
              invoice is voided and reissued.
            </span>
          </div>
        </CardFooter>
      </Card>
    </div>
  );
}

function NewCustomer({
  organizationId,
  onCreated,
  onCancel,
}: {
  organizationId: string;
  onCreated: (row: CustomerRow) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [postal, setPostal] = useState('');
  const [exempt, setExempt] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const create = useMutation({
    mutationFn: () =>
      createCustomer(organizationId, {
        displayName: name.trim(),
        kind: 'residential',
        primaryEmail: email.trim() === '' ? null : email.trim(),
        primaryPhone: phone.trim() === '' ? null : phone.trim(),
        billingAddressLine1: address.trim() === '' ? null : address.trim(),
        billingCity: city.trim() === '' ? null : city.trim(),
        billingPostalCode: postal.trim() === '' ? null : postal.trim(),
        isTaxExempt: exempt,
      }),
    onSuccess: onCreated,
    onError: (caught) =>
      setError(caught instanceof Error ? caught.message : 'The customer was not added.'),
  });
  return (
    <div className="flex flex-col gap-3 rounded-[var(--radius-control)] border border-border p-3">
      <p className="text-sm font-medium">New customer</p>
      {error === null ? null : <Alert tone="danger">{error}</Alert>}
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Name" required>
          {({ inputProps }) => (
            <Input {...inputProps} value={name} onChange={(event) => setName(event.target.value)} />
          )}
        </Field>
        <Field label="Email">
          {({ inputProps }) => (
            <Input
              {...inputProps}
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          )}
        </Field>
        <Field label="Phone">
          {({ inputProps }) => (
            <Input
              {...inputProps}
              value={phone}
              onChange={(event) => setPhone(event.target.value)}
            />
          )}
        </Field>
        <Field label="Address">
          {({ inputProps }) => (
            <Input
              {...inputProps}
              value={address}
              onChange={(event) => setAddress(event.target.value)}
            />
          )}
        </Field>
        <Field label="City">
          {({ inputProps }) => (
            <Input {...inputProps} value={city} onChange={(event) => setCity(event.target.value)} />
          )}
        </Field>
        <Field label="Postal code">
          {({ inputProps }) => (
            <Input
              {...inputProps}
              value={postal}
              onChange={(event) => setPostal(event.target.value)}
            />
          )}
        </Field>
      </div>
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={exempt}
          onChange={(event) => setExempt(event.target.checked)}
        />
        Tax exempt (status Indian, diplomat, or zero-rated customer)
      </label>
      <div className="flex gap-2">
        <Button
          size="sm"
          onClick={() => create.mutate()}
          isLoading={create.isPending}
          disabled={name.trim() === ''}
        >
          Add customer
        </Button>
        <Button size="sm" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

function IssuedInvoice({
  organizationId,
  document,
}: {
  organizationId: string;
  document: InvoiceDocument;
}) {
  const queryClient = useQueryClient();
  const [reason, setReason] = useState('');
  const [voiding, setVoiding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { invoice } = document;
  const doVoid = useMutation({
    mutationFn: () => voidInvoice(organizationId, invoice.id, reason.trim()),
    onSuccess: () => {
      setVoiding(false);
      setError(null);
      void queryClient.invalidateQueries({ queryKey: invoiceKeys.all(organizationId) });
      void queryClient.invalidateQueries({ queryKey: ['reports', organizationId] });
      void queryClient.invalidateQueries({ queryKey: ['bookkeeping', organizationId] });
    },
    onError: (caught) =>
      setError(caught instanceof Error ? caught.message : 'The invoice was not voided.'),
  });

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <Link to="/invoices" className="text-sm text-content-muted hover:text-content">
            ← Invoices
          </Link>
          <h1 className="mt-1 text-xl font-semibold text-content">
            Invoice {invoice.invoiceNumber ?? ''}
          </h1>
          <p className="text-sm text-content-muted">
            {INVOICE_STATUS_LABELS[invoice.status]}
            {invoice.status === 'void' && invoice.voidReason !== null
              ? `: ${invoice.voidReason}`
              : ''}
            {invoice.owedMinor !== '0' && invoice.status !== 'void'
              ? ` · ${formatMinor(invoice.owedMinor)} owed${(invoice.daysOverdue ?? 0) > 0 ? `, ${String(invoice.daysOverdue)} days past due` : ''}`
              : ''}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/invoices/$invoiceId/print"
            params={{ invoiceId: invoice.id }}
            className="inline-flex h-10 items-center rounded-[var(--radius-control)] bg-brand-600 px-4 text-sm font-medium text-white hover:bg-brand-700"
          >
            Print or save as PDF
          </Link>
          {invoice.transactionId === null ? null : (
            <Link
              to="/bookkeeping/$recordId"
              params={{ recordId: invoice.transactionId }}
              className="text-sm text-brand-700 hover:underline"
            >
              Its record
            </Link>
          )}
        </div>
      </div>

      {error === null ? null : <Alert tone="danger">{error}</Alert>}

      <Card>
        <CardBody className="overflow-x-auto p-0">
          <InvoiceDocumentView document={document} />
        </CardBody>
      </Card>

      {invoice.status === 'void' ? null : (
        <Card>
          <CardHeader
            title="Void"
            description="An issued invoice is never edited. Voiding reverses its income record and keeps the number; issue another for the corrected amount."
          />
          <CardBody>
            {!voiding ? (
              <Button variant="secondary" onClick={() => setVoiding(true)}>
                Void this invoice
              </Button>
            ) : (
              <div className="flex flex-wrap items-end gap-2">
                <div className="min-w-72 flex-1">
                  <Field label="Why">
                    {({ inputProps }) => (
                      <Input
                        {...inputProps}
                        value={reason}
                        onChange={(event) => setReason(event.target.value)}
                        placeholder="Billed the wrong unit; reissued as the next number"
                      />
                    )}
                  </Field>
                </div>
                <Button
                  variant="danger"
                  onClick={() => doVoid.mutate()}
                  isLoading={doVoid.isPending}
                  disabled={reason.trim().length < 5}
                >
                  Void
                </Button>
                <Button variant="ghost" onClick={() => setVoiding(false)}>
                  Keep it
                </Button>
              </div>
            )}
          </CardBody>
        </Card>
      )}
    </div>
  );
}
